/**
 * Normalize whatever a caller passes as "the current selection" into a plain
 * array of user-id strings.
 *
 * Split out of `MultiUserSelect.tsx` (module-size lane L17). Behaviour is
 * pinned by `tests/components-ui/MultiUserSelect.test.tsx`.
 *
 * The prop is DECLARED `string[] | null`, but call sites also hand it relation
 * data (`{ id, first_name, ... }` objects), a single id, or a single object —
 * which is why this exists at all. Accepting `unknown` here states that
 * honestly instead of hiding it behind `any`.
 */

/** Extract a non-empty string id from a single entry, or null if there isn't one. */
function toUserId(item: unknown): string | null {
  if (typeof item === "string" && item.length > 0) return item;
  if (typeof item === "object" && item !== null && "id" in item) {
    const { id } = item as { id: unknown };
    return typeof id === "string" && id.length > 0 ? id : null;
  }
  return null;
}

/**
 * Handles: arrays, null, undefined, single strings, and user objects with
 * `{ id: "..." }`. Anything else contributes nothing.
 */
export function normalizeUserIds(value: unknown): string[] {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map(toUserId).filter((id): id is string => id !== null);
  }
  const single = toUserId(value);
  return single ? [single] : [];
}
